import React from 'react';
import { Review } from '../../services/reviewService';

interface ReviewSectionProps {
  reviews: Review[];
  editHistory: any[];
  loadingReviews: boolean;
  isSubmittingReview: boolean;
  onSubmitReview: (editorVersionId: string) => void;
  formatTimeAgo: (timestamp: string) => string;
  getStatusLabel: (status: string) => string;
  getStatusColor: (status: string) => string;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({
  reviews,
  editHistory,
  loadingReviews,
  isSubmittingReview,
  onSubmitReview,
  formatTimeAgo,
  getStatusLabel,
  getStatusColor
}) => {
  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-white mb-6">Review</h2>
        
        {loadingReviews ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full"></div>
          </div>
        ) : reviews.length === 0 && editHistory.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">No edits to review</p>
            <p className="text-sm text-gray-500">Make some changes to your game first</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Submit Button for Latest Edit (if not already reviewed) */}
            {editHistory.length > 0 && !reviews.some(r => r.editor_edit_id === editHistory[0]?.id) && (() => {
              const latestEdit = editHistory[0];
              const versionNumber = reviews.length + 1;
              
              return (
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-white">Version {versionNumber}</h3>
                    <span className="text-sm font-medium text-gray-400">Not Reviewed</span>
                  </div>
                  
                  {latestEdit && (
                    <div className="flex items-center gap-2 mb-2 text-gray-300">
                      <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center text-xs">
                        {latestEdit.model === 'norm' ? 'B' : latestEdit.model === 'exp' ? 'O' : latestEdit.model === 'eco' ? 'S' : 'U'}
                      </div>
                      <span className="text-sm">
                        {latestEdit.model === 'norm' ? 'Bee' : latestEdit.model === 'exp' ? 'Owl' : latestEdit.model === 'eco' ? 'Squirrel' : 'User'}
                      </span>
                      <span className="text-sm text-gray-400">{latestEdit.summary}</span>
                      <span className="text-xs text-gray-500">{formatTimeAgo(latestEdit.timestamp)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-end mt-3">
                    <button 
                      onClick={() => onSubmitReview(latestEdit.id)}
                      disabled={isSubmittingReview}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {isSubmittingReview ? 'Submitting...' : 'Submit for Review'}
                    </button>
                  </div>
                </div>
              );
            })()}
            
            {/* Existing Reviews from Database */}
            {reviews.map((review, index) => {
              // Find the corresponding editor version from edit history
              const version = editHistory.find(v => v.id === review.editor_edit_id);
              const reviewNumber = reviews.length - index;
              
              return (
                <div key={review.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-white">Version {reviewNumber}</h3>
                    <span className={`text-sm font-medium ${getStatusColor(review.status)}`}>
                      {getStatusLabel(review.status)}
                    </span>
                  </div>
                  
                  {version && (
                    <div className="flex items-center gap-2 mb-2 text-gray-300">
                      <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center text-xs">
                        {version.model === 'norm' ? 'B' : version.model === 'exp' ? 'O' : version.model === 'eco' ? 'S' : 'U'}
                      </div>
                      <span className="text-sm">
                        {version.model === 'norm' ? 'Bee' : version.model === 'exp' ? 'Owl' : version.model === 'eco' ? 'Squirrel' : 'User'}
                      </span>
                      <span className="text-sm text-gray-400">{version.summary}</span>
                      <span className="text-xs text-gray-500">{formatTimeAgo(review.created_at)}</span>
                    </div>
                  )}
                  
                  {/* Review Status */}
                  {review.status === 'in_progress' && review.progress && (
                    <div className="bg-gray-700 rounded-lg p-4 mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-300">
                          {review.progress.currentStep?.name || 'In Review'}
                        </span>
                        {review.progress.currentStep && (
                          <span className="text-sm text-gray-400">
                            Step {review.progress.completed + 1} of {review.progress.total}
                          </span>
                        )}
                      </div>
                      {review.progress.currentStep?.message && (
                        <div className="text-sm text-gray-400 mb-2">{review.progress.currentStep.message}</div>
                      )}
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${review.progress.currentStep?.progress || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {review.status === 'passed' && (
                    <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between mt-3">
                      <span className="text-sm font-medium text-gray-300">Review Passed</span>
                      <button 
                        onClick={onPublish}
                        disabled={isPublishing}
                        className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-green-800 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        {isPublishing ? 'Publishing...' : 'Publish'}
                      </button>
                    </div>
                  )}
                  
                  {review.status === 'published' && (
                    <div className="bg-gray-700 rounded-lg p-4 flex items-center justify-between mt-3">
                      <span className="text-sm font-medium text-gray-400">
                        {review.published_at ? `Published ${formatTimeAgo(review.published_at)}` : 'Published'}
                      </span>
                      <div className="flex items-center gap-2 text-green-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm font-medium">Live</span>
                      </div>
                    </div>
                  )}
                  
                  {review.status === 'outdated' && (
                    <div className="bg-gray-700/50 rounded-lg p-4 mt-3">
                      <span className="text-sm text-gray-500">This version has been superseded by a more recent review</span>
                    </div>
                  )}
                  
                  {review.status === 'failed' && review.result && (
                    <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 mt-3">
                      <div className="text-sm font-medium text-gray-300 mb-2">Review Found Issues</div>
                      {review.result.issues && Array.isArray(review.result.issues) ? (
                        <ul className="space-y-1 text-sm text-gray-400">
                          {review.result.issues.map((issue: string, idx: number) => (
                            <li key={idx}>- {issue}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400">{review.result.message || 'Review failed'}</p>
                      )}
                    </div>
                  )}
                  
                  {review.status === 'pending' && (
                    <div className="flex justify-end mt-3">
                      <button 
                        onClick={() => onSubmitReview(review.editor_edit_id)}
                        disabled={isSubmittingReview}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        {isSubmittingReview ? 'Submitting...' : 'Review'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewSection;

